import React, { useState, useEffect } from "react";
import {
  Plus,
  Send,
  Download,
  CheckCircle,
  XCircle,
  MessageSquare,
  BarChart3,
  Eye,
  Trash2,
  Users,
  X,
  Calendar,
  Smartphone,
  Edit,
  TrendingUp,
  Clock,
  Filter,
  AlertTriangle,
} from "lucide-react";
import CreateCampaignForm from "../components/CreateCampaignForm";
import EditCampaignForm from "../components/EditCampaignForm";
import CampaignFilter from "../components/CampaignFilter";
import CampaignPagination from "../components/CampaignPagination";
import AbortCampaignModal from "../components/AbortCampaignModal";
import RescheduleCampaignModal from "../components/RescheduleCampaignModal";
import UpdateScheduleModal from "../components/UpdateScheduleModal";
import ChangeWhatsappAccountModal from "../components/ChangeWhatsappAccountModal";
import ValidateCampaignModal from "../components/ValidateCampaignModal";
import {
  getCampaigns,
  deleteCampaign,
  sendCampaign,
  cancelScheduledCampaign,
} from "../apis/campaign";
import { requestCampaignLogs } from "../apis/downloadCenter";
import { getTemplates } from "../apis/template";
import { toast } from "react-toastify";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";

const Campaigns = () => {
  const [view, setView] = useState("list");
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [filters, setFilters] = useState({
    status: "",
    campaign_name: "",
    template_id: "",
    whatsapp_account_id: "",
    date_from: "",
    date_to: "",
  });

  const [templates, setTemplates] = useState([]);
  const [whatsappAccounts, setWhatsappAccounts] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const [abortModal, setAbortModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [rescheduleModal, setRescheduleModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [updateScheduleModal, setUpdateScheduleModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [changeAccountModal, setChangeAccountModal] = useState({
    isOpen: false,
    campaign: null,
  });
  const [validateModal, setValidateModal] = useState({
    isOpen: false,
    campaign: null,
  });

  const { selectedPhoneAccount } = usePhoneAccount();

  useEffect(() => {
    loadFilterOptions();
    loadCampaigns();
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [currentPage, perPage]);

  const loadFilterOptions = async () => {
    try {
      setLoadingFilters(true);
      const token = localStorage.getItem("userAccessToken");

      // Use selected account from context (updated when user changes account in header)
      const wabaId = selectedPhoneAccount?.wabaId || null;

      try {
        const templatesResponse = await getTemplates(token, {
          limit: 1000,
          offset: 0,
        });
        const rawTemplates =
          templatesResponse.data.metaTemplates ||
          templatesResponse.data.templates ||
          [];
        setTemplates(rawTemplates);
      } catch (err) {
        console.error("Error loading templates:", err);
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/whatsapp-accounts`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await response.json();
        if (data.success && data.data) {
          const flattened = [];
          data.data.forEach((account) => {
            if (account.phone_numbers && Array.isArray(account.phone_numbers)) {
              account.phone_numbers.forEach((phone) => {
                flattened.push({
                  id: account.id,
                  phone_id: phone.phone_id || account.phone_id,
                  phone_number: phone.phone_number,
                  business_name: account.business_name || account.waba_name,
                  waba_name: account.waba_name,
                });
              });
            } else if (account.phone_id) {
              flattened.push({
                id: account.id,
                phone_id: account.phone_id,
                phone_number: account.phone_number,
                business_name: account.business_name || account.waba_name,
                waba_name: account.waba_name,
              });
            }
          });
          setWhatsappAccounts(flattened);
        }
      } catch (err) {
        console.error("Error loading WhatsApp accounts:", err);
      }
    } catch (error) {
      console.error("Error loading filter options:", error);
    } finally {
      setLoadingFilters(false);
    }
  };

  const loadCampaigns = async (filtersToUse = null, pageToUse = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("userAccessToken");

      const activeFilters = filtersToUse !== null ? filtersToUse : filters;
      const activePage = pageToUse !== null ? pageToUse : currentPage;

      const params = {
        page: activePage,
        limit: perPage,
        includeAnalytics: "true",
      };

      if (activeFilters.status) params.status = activeFilters.status;
      if (activeFilters.campaign_name)
        params.campaign_name = activeFilters.campaign_name;
      if (activeFilters.template_id)
        params.template_id = activeFilters.template_id;
      if (activeFilters.whatsapp_account_id)
        params.whatsapp_account_id = activeFilters.whatsapp_account_id;
      if (activeFilters.date_from) params.date_from = activeFilters.date_from;
      if (activeFilters.date_to) params.date_to = activeFilters.date_to;

      const response = await getCampaigns(token, params);

      setCampaigns(response.data.data || []);

      if (response.data.pagination) {
        setTotalItems(response.data.pagination.total_items || 0);
        setTotalPages(response.data.pagination.total_pages || 0);
      } else {
        setTotalItems(response.data.data?.length || 0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error loading campaigns:", error);
      toast.error(error.response?.data?.error || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilter = (filtersToApply) => {
    const filtersToUse = filtersToApply || filters;
    setFilters(filtersToUse);
    setCurrentPage(1);
    loadCampaigns(filtersToUse, 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) {
      return;
    }
    try {
      const token = localStorage.getItem("userAccessToken");
      await deleteCampaign(token, campaignId);
      toast.success("Campaign deleted successfully");
      if (campaigns.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        loadCampaigns();
      }
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error(error.response?.data?.error || "Failed to delete campaign");
    }
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem("userAccessToken");
      await sendCampaign(token, campaignId);
      toast.success("Campaign sending started");
      loadCampaigns();
    } catch (error) {
      console.error("Error sending campaign:", error);
      toast.error(error.response?.data?.error || "Failed to start campaign");
    }
  };

  const handleDownloadLogs = async (campaignId, campaignName) => {
    try {
      const token = localStorage.getItem("userAccessToken");
      await requestCampaignLogs(token, campaignId);
      toast.success(
        `Download request created! Check Download Center for logs.`,
        { autoClose: 4000 }
      );
    } catch (error) {
      console.error("Error requesting campaign logs:", error);
      toast.error(
        error.response?.data?.error || "Failed to request campaign logs"
      );
    }
  };

  const handleAbortCampaign = (campaign) => {
    setAbortModal({ isOpen: true, campaign });
  };

  const handleRescheduleCampaign = (campaign) => {
    setRescheduleModal({ isOpen: true, campaign });
  };

  const handleUpdateSchedule = (campaign) => {
    setUpdateScheduleModal({ isOpen: true, campaign });
  };

  const handleChangeWhatsappAccount = (campaign) => {
    setChangeAccountModal({ isOpen: true, campaign });
  };

  const handleValidateCampaign = (campaign) => {
    setValidateModal({ isOpen: true, campaign });
  };

  const handleCancelScheduledCampaign = async (campaignId) => {
    if (
      !window.confirm(
        "Are you sure you want to cancel this scheduled campaign?"
      )
    ) {
      return;
    }
    try {
      const token = localStorage.getItem("userAccessToken");
      const response = await cancelScheduledCampaign(token, campaignId);
      if (response.data.success) {
        toast.success(
          response.data.message || "Scheduled campaign cancelled successfully"
        );
        loadCampaigns();
      }
    } catch (error) {
      console.error("Error cancelling scheduled campaign:", error);
      toast.error(
        error.response?.data?.error || "Failed to cancel scheduled campaign"
      );
    }
  };

  const handleModalSuccess = () => {
    loadCampaigns();
  };

  const handleCampaignSubmit = async (campaignData) => {
    try {
      const token = localStorage.getItem("userAccessToken");
      const { createCampaign } = await import("../apis/campaign");
      const response = await createCampaign(token, campaignData);
      toast.success("Campaign created successfully!");
      setView("list");
      loadCampaigns();
    } catch (error) {
      console.error("Error creating campaign:", error);
      const errorResponse = error.response?.data;

      if (errorResponse?.validation?.errors) {
        throw error;
      }

      // Don't show error for subscription issues - SubscriptionGate handles it
      if (!errorResponse?.requiresSubscription) {
        const errorMessage =
          errorResponse?.error || error.message || "Failed to create campaign";
        toast.error(errorMessage);
      }
      throw error;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-600", border: "border-emerald-200" },
      active: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-600", border: "border-blue-200" },
      scheduled: { bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-600", border: "border-purple-200" },
      cancelled: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-600", border: "border-red-200" },
      aborted: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-600", border: "border-red-200" },
      pending: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-600", border: "border-amber-200" },
    };
    return badges[status] || { bg: "bg-gray-100", text: "text-gray-700", dot: "bg-gray-600", border: "border-gray-200" };
  };

  if (view === "create") {
    return (
      <CreateCampaignForm
        onCancel={() => setView("list")}
        onSubmit={handleCampaignSubmit}
      />
    );
  }

  if (view === "edit" && editingCampaignId) {
    return (
      <EditCampaignForm
        campaignId={editingCampaignId}
        onCancel={() => {
          setView("list");
          setEditingCampaignId(null);
        }}
        onSuccess={() => {
          loadCampaigns();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#fff] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your WhatsApp campaigns</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all shadow-sm border ${
                  showFilters
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-900 border-gray-300 hover:shadow-md"
                }`}
              >
                <Filter size={16} />
                Filters
              </button>
              <button
                onClick={() => setView("create")}
                className="inline-flex items-center gap-2 text-gray-900 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-sm hover:shadow-md border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <Plus size={16} />
                Create Campaign
              </button>
            </div>
          </div>

          {selectedPhoneAccount && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3">
              <p className="text-sm text-blue-900 font-medium">
                <strong>Active Account:</strong> {selectedPhoneAccount.phoneName} ({selectedPhoneAccount.phoneNumber})
              </p>
            </div>
          )}
        </div>

        {showFilters && (
          <div className="mb-4">
            <CampaignFilter
              filters={filters}
              onFilterChange={handleFilterChange}
              onApplyFilter={handleApplyFilter}
              templates={templates}
              whatsappAccounts={whatsappAccounts}
              loading={loadingFilters}
            />
          </div>
        )}

        {/* Campaigns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full rounded-2xl border border-gray-200 p-12 text-center" style={{ backgroundColor: "#e9eeea" }}>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4 border border-gray-200">
                <div className="animate-spin h-8 w-8 border-3 border-gray-300 border-t-gray-900 rounded-full"></div>
              </div>
              <p className="text-sm text-gray-700 font-medium">Loading campaigns...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="col-span-full rounded-2xl border border-gray-200 p-12 text-center" style={{ backgroundColor: "#e9eeea" }}>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4 border border-gray-300">
                <BarChart3 className="w-8 h-8 text-gray-900" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                No campaigns found
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Create your first campaign to get started
              </p>
              <button
                onClick={() => setView("create")}
                className="inline-flex items-center gap-2 text-gray-900 px-5 py-3 rounded-xl font-semibold text-sm transition-all shadow-md hover:shadow-lg border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <Plus size={18} />
                Create Campaign
              </button>
            </div>
          ) : (
            campaigns.map((campaign) => {
              const statusBadge = getStatusBadge(campaign.status);

              return (
                <div
                  key={campaign.campaign_id}
                  className="rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all overflow-hidden bg-white"
                >
                  {/* Card Header - Now White Background */}
                  <div className="px-4 py-3 border-b border-gray-200 bg-[#e9eeea]">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-sm font-bold text-gray-900 truncate flex-1">
                        {campaign.campaign_name}
                      </h3>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${statusBadge.dot}`}></span>
                        {campaign.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {campaign.segment_name && (
                        <p className="text-xs text-gray-700 flex items-center gap-1.5 truncate font-medium">
                          <Users size={12} />
                          {campaign.segment_name}
                        </p>
                      )}
                      <p className="text-xs text-gray-700 flex items-center gap-1.5 truncate font-medium">
                        <MessageSquare size={12} />
                        {campaign.template_name || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 bg-white">
                    {/* Main Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="text-center p-2.5 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <Send size={11} className="text-blue-600" />
                          <p className="text-xs text-blue-700 font-bold">Sent</p>
                        </div>
                        <p className="text-base font-bold text-blue-900">
                          {campaign.sent_count || campaign.analytics?.status_counts?.sent || 0}
                        </p>
                      </div>
                      <div className="text-center p-2.5 bg-emerald-50 rounded-xl border border-emerald-200">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <CheckCircle size={11} className="text-emerald-600" />
                          <p className="text-xs text-emerald-700 font-bold">Delivered</p>
                        </div>
                        <p className="text-base font-bold text-emerald-900">
                          {campaign.delivered_count || campaign.analytics?.status_counts?.delivered || 0}
                        </p>
                      </div>
                      <div className="text-center p-2.5 bg-red-50 rounded-xl border border-red-200">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <XCircle size={11} className="text-red-600" />
                          <p className="text-xs text-red-700 font-bold">Failed</p>
                        </div>
                        <p className="text-base font-bold text-red-900">
                          {campaign.failed_count || campaign.analytics?.status_counts?.failed || 0}
                        </p>
                      </div>
                    </div>

                    {/* Additional Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="text-center p-2 bg-purple-50 rounded-xl border border-purple-200">
                        <div className="flex items-center justify-center gap-1 mb-0.5">
                          <Eye size={10} className="text-purple-600" />
                          <p className="text-xs text-purple-700 font-bold">Read</p>
                        </div>
                        <p className="text-sm font-bold text-purple-900">
                          {campaign.read_count || campaign.analytics?.status_counts?.read || 0}
                        </p>
                      </div>
                      <div className="text-center p-2 bg-orange-50 rounded-xl border border-orange-200">
                        <div className="flex items-center justify-center gap-1 mb-0.5">
                          <MessageSquare size={10} className="text-orange-600" />
                          <p className="text-xs text-orange-700 font-bold">Replies</p>
                        </div>
                        <p className="text-sm font-bold text-orange-900">
                          {campaign.reply_count || campaign.analytics?.reply_count || 0}
                        </p>
                      </div>
                      <div className="text-center p-2 bg-gray-100 rounded-xl border border-gray-200">
                        <div className="flex items-center justify-center gap-1 mb-0.5">
                          <Users size={10} className="text-gray-700" />
                          <p className="text-xs text-gray-700 font-bold">Total</p>
                        </div>
                        <p className="text-sm font-bold text-gray-900">
                          {campaign.analytics?.total_messages || campaign.total_contacts || 0}
                        </p>
                      </div>
                    </div>

                    {/* Metrics */}
                    {campaign.analytics?.metrics && (
                      <div className="grid grid-cols-3 gap-2 mb-3 pb-3 border-b border-gray-200">
                        <div className="text-center">
                          <p className="text-xs text-gray-600 mb-1 font-medium">Success</p>
                          <div className="flex items-center justify-center gap-1">
                            <TrendingUp size={11} className="text-emerald-600" />
                            <p className="text-sm font-bold text-emerald-600">
                              {campaign.analytics.metrics.success_rate?.toFixed(1) || 0}%
                            </p>
                          </div>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-600 mb-1 font-medium">Delivery</p>
                          <div className="flex items-center justify-center gap-1">
                            <TrendingUp size={11} className="text-blue-600" />
                            <p className="text-sm font-bold text-blue-600">
                              {campaign.analytics.metrics.delivery_rate?.toFixed(1) || 0}%
                            </p>
                          </div>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-600 mb-1 font-medium">Failure</p>
                          <div className="flex items-center justify-center gap-1">
                            <AlertTriangle size={11} className="text-red-600" />
                            <p className="text-sm font-bold text-red-600">
                              {campaign.analytics.metrics.failure_rate?.toFixed(1) || 0}%
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Schedule Info */}
                    {(campaign.scheduled_time_original || campaign.scheduled_at) && (
                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-2.5 mb-3">
                        <p className="text-xs text-purple-900 flex items-center gap-1.5 font-medium">
                          <Calendar size={12} />
                          <span className="truncate">
                            {campaign.scheduled_time_original ||
                              new Date(campaign.scheduled_at).toLocaleString()}
                            {campaign.timezone && (
                              <span className="ml-1 text-purple-700">({campaign.timezone})</span>
                            )}
                          </span>
                        </p>
                      </div>
                    )}

                    {/* Pending Alert */}
                    {(campaign.pending_count || campaign.analytics?.status_counts?.pending) > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 mb-3">
                        <p className="text-xs text-amber-900 flex items-center gap-1.5 font-medium">
                          <Clock size={12} />
                          <span>
                            Pending: {campaign.pending_count || campaign.analytics?.status_counts?.pending}
                          </span>
                        </p>
                      </div>
                    )}

                    {/* Actions Row */}
                    <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-200">
                      <div className="text-xs text-gray-600 font-medium">
                        {new Date(campaign.created_at).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1">
                        {(campaign.status === "active" || campaign.status === "scheduled") && (
                          <button
                            onClick={() => handleAbortCampaign(campaign)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Abort"
                          >
                            <X size={14} />
                          </button>
                        )}
                        {campaign.status === "active" && (
                          <button
                            onClick={() => handleSendCampaign(campaign.campaign_id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Send"
                          >
                            <Send size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => handleValidateCampaign(campaign)}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Validate"
                        >
                          <CheckCircle size={14} />
                        </button>
                        {(campaign.status === "scheduled" ||
                          campaign.status === "cancelled" ||
                          campaign.status === "aborted" ||
                          campaign.status === "pending" ||
                          campaign.status === "inactive") && (
                          <button
                            onClick={() => {
                              setEditingCampaignId(campaign.campaign_id);
                              setView("edit");
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDownloadLogs(campaign.campaign_id, campaign.campaign_name)}
                          className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteCampaign(campaign.campaign_id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Conditional Action Buttons */}
                    {(campaign.status === "scheduled" ||
                      (campaign.status === "cancelled" || campaign.status === "aborted") && campaign.scheduled_time_original ||
                      campaign.status === "pending") && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
                        {campaign.status === "scheduled" && (
                          <>
                            <button
                              onClick={() => handleUpdateSchedule(campaign)}
                              className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-purple-700 bg-purple-50 rounded-xl hover:bg-purple-100 border border-purple-200 transition-colors"
                            >
                              <Calendar size={12} />
                              Update Schedule
                            </button>
                            <button
                              onClick={() => handleCancelScheduledCampaign(campaign.campaign_id)}
                              className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-red-700 bg-red-50 rounded-xl hover:bg-red-100 border border-red-200 transition-colors"
                            >
                              <X size={12} />
                              Cancel
                            </button>
                          </>
                        )}
                        {(campaign.status === "cancelled" || campaign.status === "aborted") &&
                         campaign.scheduled_time_original && (
                          <button
                            onClick={() => handleRescheduleCampaign(campaign)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-xl hover:bg-emerald-100 border border-emerald-200 transition-colors"
                          >
                            <Calendar size={12} />
                            Reschedule
                          </button>
                        )}
                        {(campaign.status === "scheduled" ||
                          campaign.status === "cancelled" ||
                          campaign.status === "aborted" ||
                          campaign.status === "pending") && (
                          <button
                            onClick={() => handleChangeWhatsappAccount(campaign)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 bg-blue-50 rounded-xl hover:bg-blue-100 border border-blue-200 transition-colors"
                          >
                            <Smartphone size={12} />
                            Change Account
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {!loading && campaigns.length > 0 && (
          <div className="mt-6">
            <CampaignPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              perPage={perPage}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <AbortCampaignModal
        isOpen={abortModal.isOpen}
        onClose={() => setAbortModal({ isOpen: false, campaign: null })}
        campaign={abortModal.campaign}
        onSuccess={handleModalSuccess}
      />
      <RescheduleCampaignModal
        isOpen={rescheduleModal.isOpen}
        onClose={() => setRescheduleModal({ isOpen: false, campaign: null })}
        campaign={rescheduleModal.campaign}
        onSuccess={handleModalSuccess}
      />
      <UpdateScheduleModal
        isOpen={updateScheduleModal.isOpen}
        onClose={() => setUpdateScheduleModal({ isOpen: false, campaign: null })}
        campaign={updateScheduleModal.campaign}
        onSuccess={handleModalSuccess}
      />
      <ChangeWhatsappAccountModal
        isOpen={changeAccountModal.isOpen}
        onClose={() => setChangeAccountModal({ isOpen: false, campaign: null })}
        campaign={changeAccountModal.campaign}
        whatsappAccounts={whatsappAccounts}
        onSuccess={handleModalSuccess}
      />
      <ValidateCampaignModal
        isOpen={validateModal.isOpen}
        onClose={() => setValidateModal({ isOpen: false, campaign: null })}
        campaign={validateModal.campaign}
      />
    </div>
  );
};

export default Campaigns;
